import { useEffect, useRef, useState } from "react";
import type { CatalogEntry, ClientTypeFamily, ClientTypeValue } from "@school-sales-ai/shared";
import { CLIENT_TYPE_PROFILES, FEATURE_KEYS } from "@school-sales-ai/shared";
import { CatalogForm } from "@/components/catalog/CatalogForm";
import { useCatalogEditor } from "@/components/catalog/useCatalogEditor";
import { useCatalogQuery } from "@/hooks/useCatalog";
import { useClientFeatures } from "@/hooks/useClientFeatures";
import { WizardFooter } from "./WizardFooter";

type Props = {
  clientType: ClientTypeValue | null;
  family: ClientTypeFamily;
  onBack: (() => void) | null;
  onDone: () => void;
  onDirtyChange: (dirty: boolean) => void;
};

const INTRO: Record<ClientTypeFamily, string> = {
  school:
    "Los programas y ciclos que el agente puede ofrecer. Es lo que el modelo elige cuando captura el interés de un prospecto, así que los nombres deben ser los que usa la escuela. Cada cambio se guarda solo.",
  business:
    "Los productos que el agente puede ofrecer. Es lo que el modelo elige cuando captura el interés de un cliente, así que los nombres deben ser los que usa el negocio. Cada cambio se guarda solo.",
};

/** A catalog nobody has filled in yet — no rows of any kind, active or not. */
function isUntouched(catalog: CatalogEntry): boolean {
  const { draft, inactiveProducts } = catalog;
  return (
    draft.products.length === 0 &&
    draft.cycles.length === 0 &&
    draft.customFields.length === 0 &&
    inactiveProducts.length === 0
  );
}

/**
 * Step 3 — the catalog, using the same `CatalogForm` the Catálogo page uses.
 *
 * No catalog code of its own by design: every row saves itself the moment it is
 * edited, exactly as on the Catálogo page, so «Continuar» only makes sure
 * nothing is still pending before it moves on.
 */
export function StepCatalog({
  clientType,
  family,
  onBack,
  onDone,
  onDirtyChange,
}: Props): React.JSX.Element {
  const { data: catalog, isPending } = useCatalogQuery();
  const editor = useCatalogEditor(catalog);
  const [flushing, setFlushing] = useState(false);
  const [unsavedHint, setUnsavedHint] = useState<string | undefined>(undefined);
  // Which catalog shape this client edits. Both hooks are keyed on the active
  // client, so step 3 reads the plan the operator chose in step 1.
  const { data: features } = useClientFeatures();
  const enrollmentEnabled = (features ?? []).includes(FEATURE_KEYS.enrollment);

  /**
   * An academy (dance classes, languages) enrolls whenever the student shows up, and
   * no business sells by school cycle, so those segments start on open enrollment
   * rather than on cycles (`CLIENT_TYPE_PROFILES[…].openEnrollment`).
   *
   * Only on a catalog nobody has filled in yet. Applying it to a stored catalog
   * would deactivate the cycles an operator had already configured. It is SAVED
   * rather than only shown, since there is no later save to carry it.
   */
  const prefilled = useRef(false);
  const { setUsesCycles } = editor;
  useEffect(() => {
    if (prefilled.current || !catalog || !editor.isReady) return;
    prefilled.current = true;
    const openEnrollment = clientType !== null && CLIENT_TYPE_PROFILES[clientType].openEnrollment;
    if (openEnrollment && catalog.draft.usesCycles && isUntouched(catalog)) {
      void setUsesCycles(false);
    }
  }, [catalog, clientType, editor.isReady, setUsesCycles]);

  const unsaved = editor.summary.unsaved;
  useEffect(() => {
    onDirtyChange(unsaved > 0);
  }, [unsaved, onDirtyChange]);

  async function handleContinue(): Promise<void> {
    setFlushing(true);
    const left = await editor.flushAll();
    setFlushing(false);
    if (left.length > 0) {
      setUnsavedHint(`Revisa los campos marcados: ${left.map((r) => r.label).join(", ")}.`);
      return;
    }
    setUnsavedHint(undefined);
    onDone();
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[13px] text-ink-500">{INTRO[family]}</p>

      <CatalogForm mode="onboarding" editor={editor} enrollmentEnabled={enrollmentEnabled} />

      <WizardFooter
        onBack={onBack}
        onContinue={() => void handleContinue()}
        canContinue={!isPending && editor.isReady}
        isBusy={flushing}
        hint={unsavedHint}
      />
    </div>
  );
}
